/**
 * Helpers for PlanAxisLabel (types.ts) — the user-entered name/color for one segment of the
 * Timeline's frozen row/column. See PlanAxisLabelCell.tsx for where these render and PlanPage.tsx
 * for where `upsertAxisLabel` is wired into the persisted `axisLabels` state.
 */
import { randomId } from './ostUtils';
import { PlanAxisLabel, PlanLineOrientation } from 'types';

/** Longest text a single axis-label segment will accept — enforced both by the input's own
 * `maxLength` (PlanAxisLabelCell.tsx) and here, so a value arriving from any other source (e.g.
 * a future .zey import) can't exceed it either. */
export const PLAN_AXIS_LABEL_MAX_LENGTH = 24;

/** Finds the label for one frozen-row/column segment, if the user has set one. */
export function findAxisLabel(labels: PlanAxisLabel[], orientation: PlanLineOrientation, index: number): PlanAxisLabel | undefined {
    return labels.find((label) => label.orientation === orientation && label.index === index);
}

/**
 * Applies a text/color change to the label for one segment, creating it if none exists yet.
 * Text is trimmed to PLAN_AXIS_LABEL_MAX_LENGTH regardless of caller. A label that ends up with
 * no text AND no color is dropped from the list entirely rather than kept around empty — so
 * clearing a segment's text (with no color set) actually removes its entry instead of leaving an
 * inert placeholder behind.
 */
export function upsertAxisLabel(
    labels: PlanAxisLabel[],
    orientation: PlanLineOrientation,
    index: number,
    changes: Partial<Pick<PlanAxisLabel, 'text' | 'color'>>,
): PlanAxisLabel[] {
    const existing = findAxisLabel(labels, orientation, index);
    const merged: PlanAxisLabel = {
        id: existing?.id ?? randomId(),
        orientation,
        index,
        text: existing?.text ?? '',
        color: existing?.color ?? null,
        ...changes,
    };
    if ('text' in changes && merged.text) merged.text = merged.text.slice(0, PLAN_AXIS_LABEL_MAX_LENGTH);

    const withoutExisting = labels.filter((label) => !(label.orientation === orientation && label.index === index));
    const isEmpty = !merged.text && !merged.color;
    return isEmpty ? withoutExisting : [...withoutExisting, merged];
}

/**
 * Drops every label for one orientation — used when a drawn line of that orientation is deleted
 * (see PlanPage's handleDeleteLine). A label's `index` is only its segment's position among the
 * OTHER lines sharing its orientation (see computeRegions, planRegions.ts), not a stable id tied
 * to any particular line, so once one of those lines is gone the remaining indices are meaningless
 * — and would otherwise silently reattach to whichever line next lands on that index (e.g.
 * deleting a line and drawing a new one of the same orientation right back). Clearing the whole
 * orientation sidesteps having to work out which index(es) actually shifted.
 */
export function clearAxisLabelsForOrientation(labels: PlanAxisLabel[], orientation: PlanLineOrientation): PlanAxisLabel[] {
    return labels.filter((label) => label.orientation !== orientation);
}
